'use client';

import { useEffect } from 'react';
import { requestRefresh } from '@/lib/refresh-gate';
import { loadBuildId } from './build-id';
import { hydrateDevice } from './live-store';
import { loadPersistedQueue, replayQueue, setReplayListener, useQueueState } from './live-queue';
import { useT } from '@/lib/locale-context';
import styles from './live-device.module.css';

/**
 * The device half of the sync engine, mounted once in the app shell
 * (docs/SYNC.md §7.2, §7.4):
 *
 *  - it adopts the device store for `<viewer>|<buildId>`, which reads the
 *    persisted records, memberships, cursors and deferred commands back after a
 *    reload and DROPS all of them when the person or the build changed;
 *  - it replays the queue whenever the device comes back online (the socket's
 *    reopen does the same, in `live.tsx`), and refreshes once a replay pass has
 *    landed something, so the surfaces read the server's rows for the acts that
 *    were deferred (docs/SYNC.md §7.3: the pending overlay clears, the record
 *    changes only when the server's row arrives);
 *  - it registers the service worker, and only where one can exist;
 *  - it renders the "N changes waiting" indicator while the queue is non-empty.
 */
export function LiveDevice({ viewer }: { viewer: string }) {
  useEffect(() => {
    setReplayListener(() => requestRefresh('queue-replay'));
    return () => setReplayListener(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    // The same id the build watchdog compares, through the one memo both read
    // (build-watchdog.ts): the record and row shapes are the build's, so a
    // deploy invalidates the device store exactly as it invalidates a stale
    // tab. An unreachable /api/build (offline) keeps the store under a stable
    // placeholder rather than dropping it.
    void loadBuildId().then(async (id) => {
      const buildId = id ?? 'offline';
      if (cancelled) return;
      await hydrateDevice(`${viewer}|${buildId}`);
      if (cancelled) return;
      await loadPersistedQueue();
      void replayQueue();
    });
    return () => {
      cancelled = true;
    };
  }, [viewer]);

  useEffect(() => {
    const onOnline = () => void replayQueue();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  useEffect(() => {
    registerServiceWorker();
  }, []);

  return <QueueIndicator />;
}

/**
 * The service worker (docs/SYNC.md §7.4, MOBILE.md §8 step 4: "a service worker
 * so the app shell itself loads offline"; docs/NOTIFICATIONS.md "Web push").
 * Registered wherever one can exist: `serviceWorker` is a SECURE-CONTEXT API
 * and the dev tier is also reached from phones over plain http on the LAN
 * (.claude/rules/ui-kit.md), where it is undefined.
 *
 * It used to be production-only; push moved that line (2026-09-20). The worker
 * is the only thing that can show a notification for a closed app, and the dev
 * tier is reachable over https at a public name, so a phone testing push there
 * needs one. The offline shell stays production-only all the same: the worker
 * asks /api/build and stands its CACHING half down in a development build
 * (public/sw.js), so Turbopack's chunk churn never meets a cache.
 */
function registerServiceWorker(): void {
  if (typeof window === 'undefined' || !window.isSecureContext) return;
  if (!('serviceWorker' in navigator)) return;
  // Never in an automated browser. A service worker outlives the page that
  // installed it and serves the next one from its cache - exactly the
  // cross-spec state .claude/rules/parallel-worktrees.md bans, and with it
  // three checklist specs went flaky under the e2e production build
  // (localhost is a secure context, so they were installing it, 2026-09-14).
  // The consequence is honest and ledgered: the offline shell is verified on a
  // real deploy, not by the suite.
  if (navigator.webdriver) return;
  void navigator.serviceWorker.register('/sw.js').catch(() => {
    // A refused registration is not a failure of the app; it only means this
    // device does not get the offline shell.
  });
}

/** The quiet count of deferred acts. Renders nothing when the queue is empty -
 *  the ordinary case, and the only one that needs no words. */
function QueueIndicator() {
  const t = useT();
  const { waiting, full } = useQueueState();
  if (waiting === 0) return null;
  return (
    <span
      className={`${styles.waiting} ${full ? styles.full : ''}`}
      role="status"
      title={full ? t('shell.queue.full') : t('shell.queue.offline')}
    >
      {waiting}
      <span className={styles.word}>&nbsp;{t('shell.queue.waiting', { count: waiting })}</span>
    </span>
  );
}
